import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./AvailabilityCalendar.css";
import { getApiBaseUrl, getAuthHeaders } from "../utils/authUtils";

/**
 * AvailabilityCalendar Component
 * Full month calendar with availability indicators and slot selection
 *
 * Props:
 * - staffId: Required - Staff member ID
 * - locationId: Required - Shop location ID
 * - serviceDuration: Optional - Service duration in minutes (default 60)
 * - onSlotSelect: Required - Callback when user selects a time slot
 * - selectedDate: Optional - Pre-selected date
 * - compactMode: Optional - Show slots inline instead of modal (default false)
 */
const AvailabilityCalendar = ({
  staffId,
  locationId,
  serviceDuration = 60,
  onSlotSelect,
  selectedDate = null,
  compactMode = false,
}) => {
  const [date, setDate] = useState(selectedDate || new Date());
  const [monthData, setMonthData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSlotSidebar, setShowSlotSidebar] = useState(false);

  // Fetch month availability data
  useEffect(() => {
    if (!staffId || !locationId) {
      setError("Staff ID and Location ID are required");
      setLoading(false);
      return;
    }

    fetchMonthAvailability();
  }, [staffId, locationId, serviceDuration, date]);

  const fetchMonthAvailability = async () => {
    try {
      setLoading(true);
      setError(null);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const monthKey = `${year}-${month}`;

      const baseURL = getApiBaseUrl();
      const response = await fetch(
        `${baseURL}/api/bookings/available-slots-month?staffId=${staffId}&locationId=${locationId}&month=${monthKey}&serviceDuration=${serviceDuration}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch availability");
      }

      const data = await response.json();
      setMonthData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle date tile click
  const handleDateClick = (clickedDate) => {
    // Format date as YYYY-MM-DD in local timezone (avoid UTC conversion)
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, "0");
    const day = String(clickedDate.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    const dayData = monthData?.days?.[dateStr];

    if (!dayData || dayData.slotsCount === 0) {
      return; // Don't allow selection of unavailable dates
    }

    setSelectedDay(dateStr);
    setAvailableSlots(dayData.slots || []);
    setShowSlotSidebar(true);
  };

  // Handle slot selection
  const handleSlotSelect = (slot) => {
    if (onSlotSelect) {
      onSlotSelect({
        date: selectedDay,
        startTime: slot.start,
        endTime: slot.end,
        slot,
      });
    }
    setShowSlotSidebar(false);
  };

  // Close sidebar
  const handleCloseSidebar = () => {
    setShowSlotSidebar(false);
    setSelectedDay(null);
  };

  // Get availability indicator for a date
  const getAvailabilityClass = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    const dayData = monthData?.days?.[dateStr];

    if (!dayData) return "unavailable";

    switch (dayData.availability) {
      case "high":
        return "high-availability"; // Green dot
      case "low":
        return "low-availability"; // Yellow dot
      case "none":
        return "no-availability"; // Gray/disabled
      default:
        return "unavailable";
    }
  };

  // Custom tile content (adds colored dots)
  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const dateStr = date.toISOString().split("T")[0];
    const dayData = monthData?.days?.[dateStr];

    if (!dayData || dayData.slotsCount === 0) {
      return null; // No indicator for unavailable dates
    }

    return (
      <div className="availability-indicator-wrapper">
        <span
          className={`availability-dot ${getAvailabilityClass(date)}`}
        ></span>
        <span className="slot-count">{dayData.slotsCount}</span>
      </div>
    );
  };

  // Disable dates with no availability
  const tileDisabled = ({ date, view }) => {
    if (view !== "month") return false;

    const dateStr = date.toISOString().split("T")[0];
    const dayData = monthData?.days?.[dateStr];

    return !dayData || dayData.slotsCount === 0;
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Convert 24-hour time to 12-hour AM/PM format
  const formatTime12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="availability-calendar-loading">
        <div className="spinner"></div>
        <p>Loading availability...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="availability-calendar-error">
        <p>⚠️ {error}</p>
        <button onClick={fetchMonthAvailability} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      className={`availability-calendar-container ${compactMode ? "compact-mode" : ""}`}
    >
      <div
        style={{
          display: compactMode ? "grid" : "block",
          gridTemplateColumns: compactMode ? "1fr 1fr" : "1fr",
          gap: compactMode ? "24px" : "0",
          alignItems: "start",
        }}
      >
        <div className="calendar-wrapper">
          <Calendar
            value={date}
            onClickDay={handleDateClick}
            onActiveStartDateChange={({ activeStartDate }) =>
              setDate(activeStartDate)
            }
            tileContent={tileContent}
            tileDisabled={tileDisabled}
            minDate={new Date()} // Cannot book in the past
            maxDate={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)} // 90 days ahead
            navigationLabel={({ date }) => (
              <span className="calendar-nav-label">
                {date.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
            )}
          />

          <div
            className="availability-legend"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            <div className="legend-item">
              <span className="availability-dot high-availability"></span>
              <span>5+ slots available</span>
            </div>
            <div className="legend-item">
              <span className="availability-dot low-availability"></span>
              <span>1-4 slots available</span>
            </div>
            <div className="legend-item">
              <span className="availability-dot no-availability"></span>
              <span>Fully booked</span>
            </div>
          </div>

          {monthData?.cached !== undefined && (
            <div className="cache-status">
              {monthData.cached ? "⚡ Cached" : "🔄 Live data"}
            </div>
          )}
        </div>

        {/* Inline Time Slots Panel (Compact Mode) */}
        {compactMode && selectedDay && (
          <div className="inline-slots-panel">
            <div className="inline-slots-header">
              <h3>Available Times</h3>
              <button className="clear-button" onClick={handleCloseSidebar}>
                Clear
              </button>
            </div>

            <div className="selected-date-display">
              {formatDisplayDate(selectedDay)}
            </div>

            <div className="slots-list inline">
              {availableSlots.length === 0 ? (
                <p className="no-slots-message">No available time slots</p>
              ) : (
                availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    className="slot-button"
                    onClick={() => handleSlotSelect(slot)}
                  >
                    <span className="slot-time">
                      {formatTime12Hour(slot.start)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Slot Selection Sidebar (Modal Mode) */}
      {!compactMode && showSlotSidebar && (
        <div className="slot-sidebar-overlay" onClick={handleCloseSidebar}>
          <div className="slot-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="slot-sidebar-header">
              <h3>Available Times</h3>
              <button className="close-button" onClick={handleCloseSidebar}>
                ✕
              </button>
            </div>

            <div className="selected-date-display">
              {formatDisplayDate(selectedDay)}
            </div>

            <div className="slots-list">
              {availableSlots.length === 0 ? (
                <p className="no-slots-message">No available time slots</p>
              ) : (
                availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    className="slot-button"
                    onClick={() => handleSlotSelect(slot)}
                  >
                    <span className="slot-time">
                      {formatTime12Hour(slot.start)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
